import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, PlusCircle, LogOut, Copy, Check, Baby, Mail, Phone, Send, Loader2, Zap, ClipboardList, Sparkles, Settings } from 'lucide-react';
import Logo from '../../components/common/Logo';
import MoonIcon from '../../components/common/MoonIcon';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import apiService from '../../services/api';

const AdminNewReportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    babyName: '',
    motherEmail: '',
    motherWhatsapp: ''
  });
  const [created, setCreated] = useState(false);
  const [reportId, setReportId] = useState('');
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    
    try {
      // Criar relatório no backend
      const newReport = await apiService.createReport({
        baby_name: formData.babyName || 'Não informado',
        mother_email: formData.motherEmail,
        email: formData.motherEmail,
        whatsapp: formData.motherWhatsapp
      });
      
      setReportId(newReport.id);
      setCreated(true);
    } catch (err) {
      console.error('Erro ao criar relatório:', err);
      setError('Erro ao criar relatório. Por favor, tente novamente.');
    } finally {
      setCreating(false);
    }
  };

  const getReportLink = () => {
    return `${window.location.origin}/report/${reportId}`;
  };

  const copyLink = () => {
    navigator.clipboard.writeText(getReportLink());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#FBF4FF]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-[#FAE0D5] p-6 hidden lg:block">
        <div className="mb-8">
          <Link to="/admin/dashboard">
            <Logo variant="v3SonoRotina" size="md" className="cursor-pointer hover:opacity-80 transition-opacity" />
          </Link>
        </div>

        <nav className="space-y-2">
          {[
            { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
            { icon: PlusCircle, label: 'Novo Relatório', path: '/admin/new' },
            { icon: ClipboardList, label: 'Inserir Manualmente', path: '/admin/manual' },
            { icon: FileText, label: 'Relatórios', path: '/admin/reports' },
            { icon: Copy, label: 'Templates', path: '/admin/templates' },
            { icon: Sparkles, label: 'Treinar IA', path: '/admin/training' },
            { icon: Settings, label: 'Editor de Formulário', path: '/admin/form-builder' }
          ].map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                location.pathname === item.path
                  ? 'bg-[#8F7AC5] text-white'
                  : 'text-[#6B5B8C] hover:bg-[#FBF4FF]'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            className="w-full justify-start text-[#9890A8] hover:text-[#30244F] hover:bg-[#FBF4FF]"
            onClick={() => navigate('/admin')}
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        {/* Mobile Header with Menu */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <Link to="/admin/dashboard">
              <Logo variant="v3SonoRotina" size="sm" />
            </Link>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/admin')}
                className="text-[#9890A8]"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {/* Mobile Navigation Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            <Link
              to="/admin/dashboard"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                location.pathname === '/admin/dashboard'
                  ? 'bg-[#8F7AC5] text-white'
                  : 'bg-white text-[#6B5B8C] border border-[#FAE0D5]'
              }`}
            >
              <LayoutDashboard className="w-4 h-4" />
              Dashboard
            </Link>
            <Link
              to="/admin/new"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                location.pathname === '/admin/new'
                  ? 'bg-[#8F7AC5] text-white'
                  : 'bg-white text-[#6B5B8C] border border-[#FAE0D5]'
              }`}
            >
              <PlusCircle className="w-4 h-4" />
              Novo
            </Link>
            <Link
              to="/admin/reports"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                location.pathname === '/admin/reports'
                  ? 'bg-[#8F7AC5] text-white'
                  : 'bg-white text-[#6B5B8C] border border-[#FAE0D5]'
              }`}
            >
              <FileText className="w-4 h-4" />
              Relatórios
            </Link>
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          {!created ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-[#8F7AC5] to-[#D98DAE] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Baby className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-2xl lg:text-3xl font-bold text-[#30244F]">Novo Relatório</h1>
                <p className="text-[#6B5B8C]">Crie um novo formulário para a mentorada preencher</p>
              </div>

              {/* Quick Plan Option */}
              <div 
                onClick={() => navigate('/admin/quick-plan')}
                className="bg-gradient-to-r from-[#F5A623]/10 to-[#D98DAE]/10 border-2 border-dashed border-[#F5A623] rounded-2xl p-4 mb-6 cursor-pointer hover:border-solid transition-all hover:shadow-md"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#F5A623] to-[#D98DAE] rounded-xl flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#30244F]">Plano Rápido</h3>
                    <p className="text-sm text-[#6B5B8C]">
                      Já tem os dados da rotina em outro formato? Crie um plano direto, sem registro online.
                    </p>
                  </div>
                  <Button className="bg-[#F5A623] hover:bg-[#D98830]">
                    Criar
                  </Button>
                </div>
              </div>

              {/* Form */}
              <div className="bg-white rounded-3xl p-8 shadow-sm space-y-6">
                <div>
                  <Label className="text-[#30244F]">Nome do bebê (opcional)</Label>
                  <p className="text-xs text-[#9890A8] mb-2">Deixe vazio se não souber ainda</p>
                  <Input
                    placeholder="Ex: Helena"
                    value={formData.babyName}
                    onChange={(e) => setFormData({ ...formData, babyName: e.target.value })}
                    className="border-[#FAE0D5] focus:border-[#8F7AC5]"
                  />
                </div>

                <div>
                  <Label className="text-[#30244F]">E-mail da mãe</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9890A8]" />
                    <Input
                      type="email"
                      placeholder="email@exemplo.com"
                      value={formData.motherEmail}
                      onChange={(e) => setFormData({ ...formData, motherEmail: e.target.value })}
                      className="pl-10 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[#30244F]">WhatsApp da mãe</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9890A8]" />
                    <Input
                      type="tel"
                      placeholder="(11) 99999-9999"
                      value={formData.motherWhatsapp}
                      onChange={(e) => setFormData({ ...formData, motherWhatsapp: e.target.value })}
                      className="pl-10 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                    {error}
                  </div>
                )}

                <Button
                  onClick={handleCreate}
                  disabled={creating}
                  className="w-full bg-[#8F7AC5] hover:bg-[#30244F] py-6 text-lg"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <PlusCircle className="w-5 h-5 mr-2" />
                      Criar Relatório
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              {/* Success */}
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              
              <h2 className="text-2xl font-bold text-[#30244F] mb-2">Relatório Criado!</h2>
              <p className="text-[#6B5B8C] mb-8">Envie o link abaixo para a mentorada preencher</p>

              {/* Link Card */}
              <div className="bg-white rounded-3xl p-6 shadow-sm mb-6">
                <Label className="text-[#30244F] block text-left mb-2">Link do Formulário</Label>
                <div className="flex gap-2">
                  <Input
                    value={getReportLink()}
                    readOnly
                    className="bg-[#FBF4FF] border-[#FAE0D5]"
                  />
                  <Button
                    onClick={copyLink}
                    className={copied ? 'bg-green-600' : 'bg-[#8F7AC5] hover:bg-[#30244F]'}
                  >
                    {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </Button>
                </div>
              </div>

              {/* WhatsApp Button */}
              {formData.motherWhatsapp && (
                <Button
                  variant="outline"
                  className="mb-4 border-green-500 text-green-600 hover:bg-green-50"
                  onClick={() => {
                    const phone = formData.motherWhatsapp.replace(/\D/g, '');
                    const message = encodeURIComponent(`Olá! 💜\n\nAqui está o link para você registrar a rotina do seu bebê:\n\n${getReportLink()}\n\nQualquer dúvida, estou aqui!`);
                    window.open(`https://wa.me/55${phone}?text=${message}`, '_blank');
                  }}
                >
                  <Send className="w-5 h-5 mr-2" />
                  Enviar pelo WhatsApp
                </Button>
              )}

              <div className="flex gap-4 justify-center">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setCreated(false);
                    setFormData({ babyName: '', motherEmail: '', motherWhatsapp: '' });
                  }}
                  className="text-[#6B5B8C]"
                >
                  Criar outro
                </Button>
                <Button
                  onClick={() => navigate('/admin/reports')}
                  className="bg-[#8F7AC5] hover:bg-[#30244F]"
                >
                  Ver Relatórios
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminNewReportPage;
